import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Form, Formik, type FormikErrors, type FormikTouched } from "formik";
import { Cta, type Config } from "kadow-jetpack-model";
import {
  updateJetpackConfigThunk,
  useJetpackConfigSelector
} from "../../../../redux/jetpackConfig";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import { FullScreenLoader } from "../../../../components/Loaders/FullScreenLoader/FullScreenLoader";
import { setError } from "../../../../redux/actions/app";
import { validateAppStoreLink } from "../../../../utils/helpers/checkAppleStoreId";
import { validateAndroidBundleId } from "../../../../utils/helpers/checkAndroidBundleId";
import {
  configAnimation,
  JETPACK_CONFIG
} from "../../../../utils/routes/routes";
import KC_Separator from "../../../../designsystem/KC_Separator/KC_Separator";
import BottomButtons from "./BottomButtons";
import { cleanSocialLinks } from "../../../../utils/helpers/cleanSocialLinks";
import { buildPathWithTrackingId } from "../../../../utils/routing/routingUtils";
import { isAcquisitionCTA } from "../../../../utils/helpers/isAcquisitionCTA";
import { tt } from "../../../../utils/i18n/i18n";
import "./styles.scss";

export interface FormikFormChildProps {
  config: Config;
  setFieldValue: (field: string, value: any) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  errors: FormikErrors<Config>;
  touched: FormikTouched<Config>;
  setFieldTouched: (field: string, value: any) => void;
}

interface FormikFormWrapperProps {
  validationSchema?: any;
  ChildComponent: React.FunctionComponent<FormikFormChildProps>;
  showSaveBar?: boolean;
  redirectTargetAfterSubmit?: string;
}

export function FormikFormWrapperForJetpackConfig(
  props: FormikFormWrapperProps
) {
  const dispatch = useDispatch();
  const jetpackConfig = useJetpackConfigSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const history = useHistory();
  const location = useLocation<{ draftConfig?: Config } | undefined>();
  const initialConfig = location.state?.draftConfig ?? jetpackConfig;

  const hasAdvancedCouponOptions = useMemo(() => {
    return !!accountToDisplay?.saasFeatures?.adc || false;
  }, [accountToDisplay?.saasFeatures?.adc]);

  const [isLoading, setIsLoading] = useState(false);

  const validateSocialValue = (values: Config): boolean => {
    if (!values.socials) {
      return true;
    }
    if (!!values.socials.appStoreId) {
      const appStoreLinkValid = validateAppStoreLink(values.socials.appStoreId);

      if (!appStoreLinkValid.isValid) {
        dispatch(setError(appStoreLinkValid.error));
      }

      return appStoreLinkValid.isValid;
    }

    if (values.socials.androidBundleId) {
      const bundleIdValid = validateAndroidBundleId(
        values.socials.androidBundleId
      );
      if (!bundleIdValid.isValid) {
        dispatch(setError(bundleIdValid.error));
      }

      return bundleIdValid.isValid;
    }

    return true;
  };

  const handleSubmit = async (values: Config) => {
    if (!accountToDisplay?.trackingID) {
      return;
    }

    setIsLoading(true);

    let copy: Config | null = { ...values };

    copy = cleanSocialLinks(copy);
    copy = setCTAsOrder(copy);

    if (copy == null) {
      setIsLoading(false);
      return;
    }

    const isValid = validateSocialValue(copy);

    if (!isValid) {
      setIsLoading(false);
      return;
    }

    if (props.redirectTargetAfterSubmit) {
      history.push(
        buildPathWithTrackingId(
          props.redirectTargetAfterSubmit,
          accountToDisplay?.trackingID
        )
      );
    } else {
      history.push(
        buildPathWithTrackingId(JETPACK_CONFIG, accountToDisplay?.trackingID)
      );
    }

    if (!hasAdvancedCouponOptions && values?.prizes?.length) {
      values?.prizes?.forEach((p) => {
        p.conditions = [tt("default_redeem_condition")];
      });
    }

    try {
      if (!!copy) {
        await dispatch(
          updateJetpackConfigThunk(
            copy,
            accountToDisplay?.trackingID,
            accountToDisplay?.token
          ) as any
        );
      }

      const isInPrizesOrLoyaltyConfigurator =
        location.pathname.indexOf(`${configAnimation}/prizes`) !== -1;

      const nextPath = isInPrizesOrLoyaltyConfigurator
        ? `${configAnimation}/prizes`
        : JETPACK_CONFIG;

      history.push(
        buildPathWithTrackingId(nextPath, accountToDisplay?.trackingID)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const setCTAsOrder = (config: Config | null) => {
    if (!config) {
      return null;
    }

    config.ctas.sort((a: Cta, b: Cta) => {
      const aIsAcquisition = isAcquisitionCTA(a.id);
      const bIsAcquisition = isAcquisitionCTA(b.id);

      if (aIsAcquisition && !bIsAcquisition) {
        return 1;
      }

      if (!aIsAcquisition && bIsAcquisition) {
        return -1;
      }

      return 0;
    });

    return config;
  };

  if (initialConfig == null) {
    return <h1>Erreur</h1>;
  }

  function mustDisplayErrors(errors: any) {
    if (errors.ctas) {
      return true;
    }

    if (Array.isArray(errors.prizes)) {
      return false;
    }

    return true;
  }

  return (
    <>
      <FullScreenLoader loading={isLoading}>
        <Formik<Config>
          initialValues={initialConfig}
          enableReinitialize={true}
          onSubmit={handleSubmit}
          validationSchema={props.validationSchema}
          validateOnBlur={true}
          validateOnChange={true}
        >
          {({
            values,
            setFieldValue,
            handleBlur,
            errors,
            touched,
            setFieldTouched
          }) => {
            const displayErrors = mustDisplayErrors(errors);
            return (
              <Form
                id="jetpack-cta-config-form"
                className="form w-100 h-100 d-flex flex-column"
              >
                <div className="flex-grow-1">
                  <props.ChildComponent
                    config={values}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                    setFieldTouched={setFieldTouched}
                  />
                </div>
                {props.showSaveBar !== false && (
                  <>
                    <KC_Separator />
                    <BottomButtons displayErrors={displayErrors} />
                  </>
                )}
              </Form>
            );
          }}
        </Formik>
      </FullScreenLoader>
    </>
  );
}
